import { arrow, autoUpdate, flip, offset, safePolygon, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react';
import { useBreakpointDown } from '@travel-frontend/core';
import { useCallback, useMemo, useState } from 'react';
import { useTracking } from '../../hooks/index.js';
const mergeEventHandlers = (userEventHandler, defaultEventHandler) => {
  return event => {
    if (typeof userEventHandler === 'function') {
      userEventHandler(event);
    }
    if (!event.defaultPrevented && typeof defaultEventHandler === 'function') {
      defaultEventHandler(event);
    }
  };
};
export const useTooltip = ({
  open: controlledOpen,
  initialOpen = false,
  placement = 'top',
  strategy = 'absolute',
  interactive = false,
  collisionDetection = true,
  offset: offsetProp = 4,
  arrowRef,
  onOpenChange: setControlledOpen,
  tooltipElement,
  tooltipText
}) => {
  const {
    trackTooltipEvent
  } = useTracking();
  const isMobile = useBreakpointDown('md');
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const floatingOffset = arrowRef?.current != null ?
  // Get half the arrow box's hypotenuse length
  Math.sqrt(2 * arrowRef.current.offsetWidth ** 2) / 2 + offsetProp : offsetProp;
  const data = useFloating({
    placement,
    open,
    strategy,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(floatingOffset), collisionDetection && flip({
      fallbackStrategy: 'initialPlacement',
      fallbackAxisSideDirection: 'start',
      crossAxis: false,
      altBoundary: true
    }), collisionDetection && shift({
      padding: 5
    }), arrowRef && arrow({
      element: arrowRef,
      padding: 16 // <Layer /> padding
    })]
  });
  const context = data.context;
  const floatingX = data.x;
  const floatingY = data.y;
  const floatingStrategy = data.strategy;
  const arrowX = data.middlewareData.arrow?.x;
  const arrowY = data.middlewareData.arrow?.y;
  const interactions = useInteractions([useHover(context, {
    enabled: controlledOpen == null,
    move: false,
    handleClose: interactive ? safePolygon({
      blockPointerEvents: true
    }) : undefined
  }), useFocus(context, {
    enabled: controlledOpen == null
  }), useDismiss(context, {
    // when using 'pointerdown' | 'mousedown' this will be closed even though the user intents to scroll
    outsidePressEvent: 'click',
    // deactivated because for whatever reason the tooltip will not reopen when clicking/hovering it after ESC was pressed
    escapeKey: false
  }), useRole(context, {
    role: 'tooltip'
  })]);
  const handleMouseEnter = useCallback(() => {
    if (!isMobile && tooltipElement && tooltipText) {
      // Track the tooltip hover event (desktop)
      trackTooltipEvent('tooltip_hovered', {
        tooltip_element: tooltipElement,
        tooltip_text: tooltipText
      });
    }
  }, [isMobile, tooltipElement, tooltipText, trackTooltipEvent]);
  const handleClick = useCallback(() => {
    if (isMobile && tooltipElement && tooltipText) {
      // Track the tooltip click event (mobile)
      trackTooltipEvent('tooltip_clicked', {
        tooltip_element: tooltipElement,
        tooltip_text: tooltipText
      });
    }
  }, [isMobile, tooltipElement, tooltipText, trackTooltipEvent]);
  // Extend getReferenceProps to include mouse and click handlers
  const getReferenceProps = useCallback(({
    onMouseEnter,
    onClick,
    ...userProps
  } = {}) => {
    return {
      ...userProps,
      ...interactions.getReferenceProps(),
      onMouseEnter: mergeEventHandlers(onMouseEnter, handleMouseEnter),
      onClick: mergeEventHandlers(onClick, handleClick)
    };
  }, [interactions, handleMouseEnter, handleClick]);
  const getFloatingStyles = useCallback(() => {
    return {
      position: floatingStrategy,
      top: floatingY ?? 0,
      left: floatingX ?? 0,
      visibility: floatingX == null ? 'hidden' : 'visible'
    };
  }, [floatingStrategy, floatingX, floatingY]);
  // https://floating-ui.com/docs/arrow
  const getArrowStyles = useCallback(() => {
    if (arrowRef?.current != null && (arrowX != null || arrowY != null)) {
      const side = data.placement.split('-')[0];
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right'
      }[side];
      // assumes arrow points to the bottom-right corner by default
      const rotation = {
        top: '45deg',
        right: '135deg',
        bottom: '225deg',
        left: '315deg'
      }[side];
      return {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
        right: '',
        bottom: '',
        // @ts-expect-error TS(2464) FIXME: A computed property name must be of type 'string',... Remove this comment to see the full error message
        [staticSide]: `${-Math.ceil(arrowRef.current.offsetWidth / 2)}px`,
        transform: `rotate(${rotation})`
      };
    }
  }, [arrowRef, arrowX, arrowY, data.placement]);
  return useMemo(() => ({
    open,
    setOpen,
    ...interactions,
    ...data,
    getFloatingStyles,
    getArrowStyles,
    getReferenceProps
  }), [open, setOpen, interactions, data, getFloatingStyles, getArrowStyles, getReferenceProps]);
};